'use client'

import { forwardRef, useId } from 'react'
import { Select } from '@base-ui/react/select'
import { LuCheck, LuChevronDown } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { useHydrated } from '@/core/hooks/use-hydrated'
import { fieldStyles } from '../field-styles'
import { choiceStyles } from './combobox.styles'
import type { ComboboxProps } from './combobox.types'

const ComboboxSelect = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      id: providedId,
      name,
      required,
      ariaLabel,
      helperText,
      error,
      disabled,
      className,
      placeholder = '선택하세요',
      emptyMessage = '선택할 항목이 없습니다.',
      variant = 'field',
      triggerProps,
      portalContainer,
    },
    ref
  ) => {
    const id = useId()
    const hydrated = useHydrated()
    const triggerId = providedId ?? `${id}-trigger`
    const messageId = error || helperText ? `${id}-message` : undefined
    const selected = options.find((option) => option.value === value)

    return (
      <Select.Root
        items={options}
        value={selected?.value ?? null}
        onValueChange={(next) => {
          if (next !== null) onChange?.(next)
        }}
        name={name}
        required={required}
        disabled={disabled || !hydrated}
      >
        <div className={cn('relative', className)}>
          {label && (
            <label htmlFor={triggerId} className={fieldStyles.label}>
              {label}
            </label>
          )}
          <Select.Trigger
            ref={ref}
            id={triggerId}
            aria-label={label ? undefined : (ariaLabel ?? placeholder)}
            aria-invalid={Boolean(error)}
            aria-describedby={messageId}
            {...triggerProps}
            className={cn(
              variant === 'ghost'
                ? choiceStyles.ghostTrigger
                : variant === 'bare'
                  ? choiceStyles.bareTrigger
                  : choiceStyles.trigger,
              error && 'border-error data-[popup-open]:border-error',
              triggerProps?.className
            )}
          >
            <Select.Value
              // Base UI 는 '' 값을 선택 없음으로 보고 placeholder 를 그린다 — 항목이 있으면 그 라벨을 보여 준다
              placeholder={selected?.label ?? placeholder}
              className={cn(
                'min-w-0 flex-1 truncate',
                !selected && 'text-description'
              )}
            />
            <Select.Icon className="ml-auto shrink-0 text-description">
              <LuChevronDown
                aria-hidden="true"
                className="size-4 transition-transform duration-200 group-data-[popup-open]:rotate-180"
              />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal container={portalContainer}>
            <Select.Positioner
              align={variant === 'ghost' ? 'end' : 'start'}
              sideOffset={6}
              alignItemWithTrigger={false}
              className={choiceStyles.positioner}
            >
              <Select.Popup
                className={cn(
                  choiceStyles.popup,
                  variant === 'ghost' && choiceStyles.ghostPopup
                )}
              >
                {options.length === 0 && (
                  <p
                    role="status"
                    className="px-4 py-4 text-c14 text-description"
                  >
                    {emptyMessage}
                  </p>
                )}
                <Select.List className={choiceStyles.list}>
                  {options.map((option) => (
                    <Select.Item
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={choiceStyles.item}
                    >
                      <Select.ItemText className="min-w-0 flex-1 wrap-break-word">
                        {option.label}
                      </Select.ItemText>
                      <Select.ItemIndicator
                        keepMounted
                        className="size-4 shrink-0 opacity-0 data-[selected]:opacity-100"
                      >
                        <LuCheck
                          aria-hidden="true"
                          className="size-4 text-point"
                        />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
          {messageId && (
            <p
              id={messageId}
              role={error ? 'alert' : undefined}
              className={error ? fieldStyles.error : fieldStyles.helper}
            >
              {error ?? helperText}
            </p>
          )}
        </div>
      </Select.Root>
    )
  }
)

ComboboxSelect.displayName = 'ComboboxSelect'
export default ComboboxSelect
